import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { palette } from 'styles/color';

type MenuItemProps = {
  onClick: () => void;
  onClose?: () => void;
  children: React.ReactNode;
  style?: React.CSSProperties;
  position?: 'top' | 'bottom';
};

const TooltipMenu = ({
  opener,
  children,
  onOpen,
  position = 'bottom'
}: {
  opener: React.ReactNode;
  children: React.ReactNode;
  onOpen?: () => void;
  position?: 'top' | 'bottom';
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
      setIsVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const closeTooltip = () => {
    setIsVisible(false);
  };

  return (
    <TooltipContainer ref={tooltipRef}>
      <TooltipButton
        onClick={() => {
          setIsVisible(!isVisible);
          onOpen && onOpen();
        }}>
        {opener}
      </TooltipButton>
      <TooltipContent isVisible={isVisible} position={position}>
        <MenuList>
          {React.Children.map(children, (child) => {
            if (React.isValidElement<MenuItemProps>(child) && child.type !== React.Fragment) {
              return React.cloneElement(child, { onClose: closeTooltip });
            }
            return child;
          })}
        </MenuList>
      </TooltipContent>
    </TooltipContainer>
  );
};

const TooltipContainer = styled.div`
  position: relative;
`;

const TooltipButton = styled.div``;

const TooltipContent = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isVisible', 'position'].includes(prop),
})<{ isVisible: boolean; position: 'top' | 'bottom' }>`
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  position: absolute;
  ${({ position }) => (position === 'top' ? 'bottom: calc(100% + 6px);' : 'top: calc(100% + 6px);')}
  right: 0;
  background: ${palette.white};
  box-shadow: 0px 0px 8px 0px #00000033;
  border-radius: 8px;
  z-index: 10;
  padding: 8px 10px;
  box-sizing: border-box;
`;
const MenuList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MenuItemContainer = styled.div`
  cursor: pointer;
  border-radius: 8px;

  &:hover {
    background: ${palette.gray[100]};
  }
  &:active {
    background-color: ${palette.gray[200]};
  }

  button.item {
    justify-content: flex-start;
    padding: 10px 8px;
    // margin-top: 4px;
  }
`;

TooltipMenu.MenuItem = ({ onClick, children, onClose, style }: MenuItemProps) => (
  <MenuItemContainer
    onClick={() => {
      onClick();
      if (onClose) onClose();
    }}
    style={style}>
    {children}
  </MenuItemContainer>
);

export default TooltipMenu;
